/**
 * Tutorial Steps Registry
 *
 * Each step shape:
 * {
 *   id: string,                     unique step id
 *   targetId: string | null,        data-tutorial-id attribute on the DOM element to spotlight
 *                                   null → centered modal (full overlay, no spotlight)
 *   title: string,                  tooltip heading
 *   description: string,            main body text
 *   note: string | null,            optional sub-note (italic, smaller)
 *   placement: 'top'|'bottom'|'left'|'right'|'center'|null,
 *                                   preferred tooltip placement (null → auto)
 * }
 *
 * Tour IDs (used as localStorage keys):
 *   'welcome_home'        — first-login tour on /home
 *   'fleet_inputs'        — first visit to /fleet/inputs
 *   'ai_advisor'          — first open of the AI Advisor drawer
 *   'fleet_step_routes'   — first time on Step 1
 *   'fleet_step_depots'   — first time on Step 2
 *   'fleet_step_chargers' — first time on Step 3
 *   'fleet_step_vehicles' — first time on Step 4
 *   'fleet_step_rates'    — first time on Step 5
 *   'fleet_step_review'   — first time on Step 6
 */

// ---------------------------------------------------------------------------
// Tour 1 — Welcome Home (/home)
// ---------------------------------------------------------------------------
export const WELCOME_HOME_TOUR = [
  {
    id: 'welcome-intro',
    targetId: null,
    title: 'Welcome to AmpTrans CHARGE-OPT',
    description:
      "CHARGE-OPT is a platform that supports electrification planning for fleets, charging providers, electric utilities, and policymakers. Take a 2-minute tour to see where everything is—and start building your electrification plan.",
    note: 'You can skip anytime.',
    placement: 'center',
  },
  {
    id: 'welcome-products',
    targetId: 'home-product-grid',
    title: 'Pick a tool to start',
    description:
      "Choose what you're working on today:\n- eFleetPlan: Fleet electrification analysis\n- ChargerPlan: Charging site selection\n- GridPlan: EV load impact (coming soon)\n- PolicyPlan: Policy and incentives analysis (invite-only)\n",
    note: 'You can switch tools later.',
    placement: null,
  },
];

// ---------------------------------------------------------------------------
// Tour 2 — Fleet Inputs (/fleet/inputs)
// ---------------------------------------------------------------------------
export const FLEET_INPUTS_TOUR = [
  {
    id: 'fleet-workflow-intro',
    targetId: null,
    title: 'The Analysis Workflow',
    description:
      'Your electrification plan is built across multiple structured steps. Each step feeds data into the next — so work through them in order for the most accurate output.',
    note: 'You can save your progress at any time and return later.',
    placement: 'center',
  },
  {
    id: 'fleet-step-nav',
    targetId: 'fleet-step-nav',
    title: 'Step Navigation',
    description:
      'These circles track where you are. Click any circle to jump to that step. The analysis runs after the final step is ready.',
    note: 'Steps 1–3 show a map view. Steps 4–6 are full-width forms.',
    placement: 'bottom',
  },
  {
    id: 'fleet-advisor-intro',
    targetId: 'fleet-advisor-btn',
    title: 'AI Advisor — Ask at Any Step',
    description:
      'AI Advisor knows which step you\'re on and reads your current draft, so you can ask "what should I enter here?" or "how will this affect my results?" without leaving your work.',
    note: null,
    placement: 'top',
  },
  {
    id: 'fleet-inputs-done',
    targetId: null,
    title: "You're Ready to Begin",
    description:
      'Work through all steps, then click "Run Analysis" on the final step to generate your electrification plan. Results appear in the Analytics section.',
    note: 'Start with Step 1: Route Selection — define the daily itineraries you want to electrify.',
    placement: 'center',
  },
];

// ---------------------------------------------------------------------------
// Step Tours — triggered on first entry to each of the 6 input steps
// ---------------------------------------------------------------------------

export const FLEET_STEP_ROUTES_TOUR = [
  {
    id: 'routes-intro',
    targetId: null,
    title: 'Step 1 — Route Selection',
    description:
      'Routes define the daily itineraries you want to electrify. Each route captures a vehicle\'s full day: origin, stops, destination, and timing. The model assumes one EV replaces one diesel vehicle per route.',
    note: 'Select the routes you want to include in this analysis case. You can add, edit, or delete routes at any time.',
    placement: 'center',
  },
  {
    id: 'routes-add-delete',
    targetId: 'route-footer-actions',
    title: 'Add & Manage Routes',
    description:
      'Click "Add Routes" to define how your fleet operates on a typical day. You can add routes individually or multiple at a time. Each route you add will be evaluated for EV feasibility and charging strategy.',
    note: null,
    placement: 'top',
  },
  {
    id: 'routes-table',
    targetId: 'route-table',
    title: 'Select Routes for Analysis',
    description:
      'Use the checkboxes to select which routes are included in this case. The analysis only runs for checked routes. Click the pencil icon next to any route name to view and edit its details — including stops, timing, and vehicle class.',
    note: 'You can filter routes by name, origin-destination (OD) pair, or vehicle class using the column filter icons.',
    placement: 'top',
  },
  {
    id: 'routes-advanced',
    targetId: 'route-advanced-settings',
    title: 'Advanced Settings — Operating Days & SoC',
    description:
      'Click "Advanced settings" to bulk-set operating days for selected routes (e.g., Mon–Fri only) and to set a minimum State of Charge (SoC) safety floor. The SoC floor ensures the analysis never runs a vehicle below that battery level at any point during the day.',
    note: 'The default SoC floor is 10%. Lower values allow more aggressive charge scheduling.',
    placement: 'bottom',
  },
  {
    id: 'routes-name-new-case',
    targetId: 'case-name-modal',
    title: 'Name Your New Case',
    description:
      'Type a name for this planning scenario, or choose one of the suggested names below. Each case saves its own routes, vehicles, and results — so you can create and compare multiple scenarios side by side.',
    note: 'Click Continue when you\'re ready to move on.',
    placement: 'top',
    noScrim: true,
  }
];

export const FLEET_STEP_DEPOTS_TOUR = [
  {
    id: 'depots-intro',
    targetId: null,
    title: 'Step 2 — Depot Configuration',
    description:
      'Depots are the home bases for your vehicles — locations where your fleet is based and where you can install your own chargers. For each depot, the model evaluates charger sizing, grid interconnection costs, and optionally solar and battery storage. A depot can be used for charging if any of your selected routes has a stop at the depot or within 2 miles of it.',
    note: null,
    placement: 'center',
  },
  {
    id: 'depots-add',
    targetId: 'depot-footer-actions',
    title: 'Add & Manage Depots',
    description:
      'Click "Add Depots" and search for an address. Only checked depots are included in the case analysis.',
    note: null,
    placement: 'top',
  },
  {
    id: 'depots-table-edit',
    targetId: 'depot-table',
    title: 'Edit Depot Details — Grid, Chargers & Solar',
    description:
      'Click the pencil icon next to any depot name to open its detail panel. This is where you can:\n\n• View nearby power grid circuits and available hosting capacity\n• Set desired charger type and count limits\n• Enable solar installation and enter available roof area\n• Enable battery storage option \n\nThese inputs directly affect infrastructure cost estimates and charging feasibility.',
    note: 'The "Grid" column shows a quick summary of nearby power grid circuits. Open the edit panel to see full circuit details.',
    placement: 'left',
  },
];

export const FLEET_STEP_CHARGERS_TOUR = [
  {
    id: 'chargers-intro',
    targetId: null,
    title: 'Step 3 — Public Charging Stations',
    description:
      'If public charging stations are checked, the model evaluates a mixed charging strategy that can include both depots and public stations. A public charging station can be used for charging if any of your selected routes has a stop at the station or within 2 miles of it.',
    note: 'You must either provide at least one depot or select public charging station(s) in order to electrify a route. This is required because we assume daily cycle operation with EVs needing to charge and start each day at the same SoC.',
    placement: 'center',
  },
  {
    id: 'chargers-select-btn',
    targetId: 'charger-select-stations-btn',
    title: 'Filter & Select Stations',
    description:
      'Click "Select Stations" to open the station filter panel. You can filter by operational status, station owner, and minimum power level (impact charging speed). Check the boxes next to stations you want to include, then click "Apply Selection" to add them to your case.',
    note: null,
    placement: 'top',
  },
  {
    id: 'chargers-show-toggle',
    targetId: 'charger-show-toggle',
    title: 'Show Selected vs. Show All',
    description:
      '"Show selected" displays only the stations you\'ve already included in this case. "Show all" shows every station in our database so you can check or uncheck them directly in the table without opening the filter panel.',
    note: null,
    placement: 'bottom',
  },
  {
    id: 'chargers-table',
    targetId: 'charger-table',
    title: 'Checkboxes Control Inclusion in Simulation',
    description:
      'Each checked station is passed to the analytical model. The model will decide whether and when to use each station based on route stops, energy needs, and cost. Unchecked stations are ignored even if they appear nearby.',
    note: 'The eye icon in each row opens a read-only details view for that station.',
    placement: 'top',
  },
];

export const FLEET_STEP_VEHICLES_TOUR = [
  {
    id: 'vehicles-intro',
    targetId: null,
    title: 'Step 4 — Vehicle Selection',
    description:
      'Select candidate EV models that could replace diesel vehicles on your routes. Each route\'s vehicle class must have at least one EV candidate. The analytical model will pick the best-fit EV from your candidates for each route.',
    note: 'You can edit the vehicle properties or even define your own candidate EV model if not listed.',
    placement: 'center',
  },
  {
    id: 'vehicles-classes',
    targetId: 'vehicle-classes-container',
    title: 'Required Vehicle Classes',
    description:
      'Your selected routes determine which vehicle classes appear here. Each card represents a class (e.g., heavy duty) and shows the EV candidates you\'ve added for that class. The top table lists candidate EVs; the bottom table shows the baseline diesel vehicle for comparison.',
    note: 'The diesel vehicle row is pre-filled with defaults. You can edit cost and efficiency fields to match your actual fleet.',
    placement: 'center',
  },
  {
    id: 'vehicles-add-btn',
    targetId: 'vehicle-add-btn',
    title: 'Add Candidate EV Models',
    description:
      'Click "Add Vehicle" to open the vehicle database. Filter by program (DOE AFDC, California HVIP) or search by make/model. Check the vehicles you want to consider, then click "Add Selected" to add them as candidates for this vehicle class.',
    note: 'You can also add a custom vehicle with your own specs using the "Your Vehicles" tab in the modal.',
    placement: 'bottom',
  },
];

export const FLEET_STEP_RATES_TOUR = [
  {
    id: 'rates-intro',
    targetId: null,
    title: 'Step 5 — Energy Rates',
    description:
      'Electricity costs are a major driver of EV total cost of ownership. This step helps you identify the utility rate each depot operates under, plus costs for public charging and fuel costs for the diesel baseline.',
    note: 'The model auto-detects the likely utility for each depot based on its address. You can override the rate if needed.',
    placement: 'center',
  },
  {
    id: 'rates-depot-rates',
    targetId: 'rate-depot-rates-card',
    title: 'Depot Utility Rates',
    description:
      'Use the "Depot" dropdown to switch between your depots. Then use the search box to browse rates from that depot\'s utility. Click a rate to select it — the detail panel on the right will show the full energy charge and demand charge tables.',
    note: 'Select the rate that most closely matches your current or expected electricity contract.',
    placement: 'top',
  },
  {
    id: 'rates-additional-costs',
    targetId: null,
    title: 'Public Charging & Diesel Costs',
    description:
      'Besides the depot rates, set your per-kWh additional markup cost for public charging (used when EVs charge at public stations) and your diesel or gasoline fuel price per gallon. These inputs directly affect the TCO comparison between EV and diesel.',
    note: 'Additional public charging costs reflects extra fees above base electricity rates—these cover station costs and profit.',
    placement: 'center',
  },
];

export const FLEET_STEP_REVIEW_TOUR = [
  {
    id: 'review-intro',
    targetId: null,
    title: 'Step 6 — Review & Run',
    description:
      'Before running the analysis, review all your inputs and choose your goal. The analytical model will find the best combination of EV models, infrastructure, and charging strategies that meets your goal, given your routes, depots, vehicles, and energy rates.',
    note: null,
    placement: 'center',
  },
  {
    id: 'review-tabs',
    targetId: 'review-tab-pills',
    title: 'Review Your Inputs',
    description:
      'Click through these tabs to verify each section of your case before running. Each tab shows a summary and a "Go to step" link if something needs to be changed. Fix any issues before continuing — you can\'t edit inputs after the run starts.',
    note: null,
    placement: 'bottom',
  },
  {
    id: 'review-goal',
    targetId: 'review-goal-section',
    title: 'Set Your Optimization Goal',
    description:
      'Choose what the analytical model should prioritize:\n\n• Minimize Cost (TCO) — lowest total cost of ownership\n• Accelerate EV Adoption — maximize the number of routes electrified\n• Minimize Emissions (GHG) — minimize greenhouse gas output\n\nThe same set of inputs produces different fleet recommendations depending on the goal you select.',
    note: null,
    placement: 'left',
  },
  {
    id: 'review-eval-mode',
    targetId: 'review-eval-mode',
    title: 'Choose Evaluation Mode',
    description:
      '"Standard Evaluation" runs quickly and is best for exploring options. "Calendar Evaluation" models charging day-by-day across a full year and produces more accurate results, especially for routes with seasonal variation — but takes longer to compute.',
    note: 'Start with Standard Evaluation while iterating, then switch to Calendar Evaluation for your final plan.',
    placement: 'left',
  },
  {
    id: 'review-start',
    targetId: 'review-start-planning-btn',
    title: 'Start Planning',
    description:
      'Click "Start Planning" to submit your case to the analytical model. If this is an existing case, you\'ll be asked whether to update it in place or save a copy as a new case. Results will appear in the Analytics section when the run is complete.',
    note: 'Analysis typically takes 1–5 minutes depending on the number of routes and evaluation mode.',
    placement: 'top',
  },
];

// ---------------------------------------------------------------------------
// Tour 3 — AI Advisor (inside the AdvisorDrawer)
// ---------------------------------------------------------------------------
export const AI_ADVISOR_TOUR = [
  {
    id: 'advisor-recommended-setup',
    targetId: null,
    title: 'Before You Start — Recommended Setup',
    description:
      'The AI Advisor helps you navigate input steps and interpret your case analysis. You can ask questions at any point.',
    note: 'The advisor reads your live draft automatically — no manual copy-paste needed.',
    placement: 'center',
  },
  {
    id: 'advisor-context-chip',
    targetId: 'advisor-surface-chip',
    title: 'Context-Aware Scope',
    description:
      'This badge shows what the advisor is currently scoped to. On input steps it reads your live draft. On results pages it reads your case output. It always knows where you are, so answers stay relevant.',
    note: null,
    placement: 'bottom',
  },
  {
    id: 'advisor-thread-controls',
    targetId: 'advisor-thread-controls',
    title: 'Conversation History',
    description:
      'Your chats are saved as threads. Use "New chat" to start a fresh topic, or select a previous thread from the dropdown to continue it. Delete threads you no longer need.',
    note: null,
    placement: 'bottom',
  },
  {
    id: 'advisor-question-area',
    targetId: 'advisor-question-area',
    title: 'Ask in Plain Language',
    description:
      'Type your question here and press Send (or Ctrl/Cmd + Enter). The advisor uses your current case context automatically.',
    note: null,
    placement: 'top',
  },
  {
    id: 'advisor-done',
    targetId: null,
    title: "You're All Set",
    description:
      'The AI Advisor is your electrification co-pilot. Use it to sanity-check assumptions, understand tradeoffs, and figure out how the plan works.',
    note: null,
    placement: 'center',
  },
];
